import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, Checkbox, FormControl, IconButton, MenuItem, OutlinedInput, Select, Stack, Switch } from "@mui/material";

import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HourglassBottomTwoToneIcon from '@mui/icons-material/HourglassBottomTwoTone';

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";

interface BillingClientsResponseModel {
    data: BillingClientsData;
    error: IError;
}
interface BillingClientsData {
    clients: string[];
}

interface CreateInvoiceRequestModel {
    month: Dayjs;
    clients: string[] | null;
}

interface CreateInvoiceResponseData {
    data: CreateInvoiceData;
    error: IError;
}
interface CreateInvoiceData {
    id: string;
}

interface CreateInvoiceProgressResponseData {
    data: CreateInvoiceProgressData;
    error: IError;
}
interface CreateInvoiceProgressData {
    status: string;
    progresses: CreateInvoiceProgressModel[];
}
interface CreateInvoiceProgressModel {
    username: string;
    status: string;
    errorDescription: string;
}

function CreateXeroInvoices() {
    const { enqueueSnackbar } = useSnackbar();

    const [isForSpecificClient, setIsForSpecificClient] = useState<boolean>(false);

    const [allClients, setAllClients] = useState<string[]>([]);
    const [specificClients, setSpecificClients] = useState<string[]>([]);

    const [month, setMonth] = useState<Dayjs>(dayjs().add(-1, 'month'));
    var monthCopy = useRef<Dayjs>(dayjs().add(-1, 'month'));

    const [status, setStatus] = useState<string>("");
    const [progress, setProgress] = useState<CreateInvoiceProgressModel[]>([]);

    function SelectForSpecificClient(value: any) {
        var checked = value.target.checked;
        setIsForSpecificClient(checked);

        if (checked && allClients.length === 0) {
            LoadClients();
        }
    }

    const LoadClients = async () => {
        try {
            var resp: BillingClientsResponseModel = await api.get("api/Billing/GetClients", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setAllClients(resp.data.clients);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function CreateBillingInvoices() {
        setStatus("In Progress");
        setProgress([]);

        if (!isForSpecificClient) {
            CreateInvoiceForAllClients();
        }
        else {
            CreateInvoiceForSpecificClient();
        }
    }

    const CreateInvoiceForAllClients = async () => {
        try {
            var bodyReq: CreateInvoiceRequestModel = {
                month: monthCopy.current,
                clients: null
            }

            var resp: CreateInvoiceResponseData = await api.post("api/Xero/CreateInvoice", {
                json: bodyReq,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                var createId = resp.data.id;
                localStorage.setItem("createInvoiceId", createId);
                StartProgress();
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const CreateInvoiceForSpecificClient = async () => {
        if (specificClients.length === 0) {
            enqueueSnackbar("Please select at least on client", { variant: "error" });
            return;
        }

        try {
            var bodyReq: CreateInvoiceRequestModel = {
                month: monthCopy.current,
                clients: specificClients
            }

            var resp: CreateInvoiceResponseData = await api.post("api/Xero/CreateInvoice", {
                json: bodyReq,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                var createId = resp.data.id;
                localStorage.setItem("createInvoiceId", createId);
                StartProgress();
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const StartProgress = async () => {
        var createId = localStorage.getItem("createInvoiceId");
        if (createId === null) {
            setStatus("Error");
            return;
        }

        setTimeout(async () => {
            try {
                const response: CreateInvoiceProgressResponseData = await api.get(`api/Xero/CreateInvoiceProgress?id=${createId?.toString()}`, {
                    headers: {
                        "Authorization": localStorage.getItem("token")!
                    }
                }).json();

                if (response.error == null) {
                    if (response.data.status === "Completed") {
                        CreateInvoiceCompleted();
                        setStatus(response.data.status);
                        setProgress(response.data.progresses);
                    }
                    else if (response.data.status === "Error") {
                        enqueueSnackbar("An error occured while creating invoices", { variant: "error" });
                        localStorage.removeItem("createInvoiceId");
                        return;
                    }
                    else {
                        setStatus(response.data.status)
                        setProgress(response.data.progresses);
                        StartProgress();
                    }
                }
                else {
                    localStorage.removeItem("createInvoiceId");
                    setStatus("Error");
                }
            }
            catch (err) {
                // in case CORS error
                StartProgress();
            }
        }, 2500);
    }

    const CreateInvoiceCompleted = async () => {
        var createId = localStorage.getItem("createInvoiceId");
        if (createId === null) {
            setStatus("Error");
            return;
        }

        try {
            const response: CreateInvoiceProgressResponseData = await api.get(`api/Xero/CreateInvoiceCompleted?id=${createId?.toString()}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error === null) {
                setStatus(response.data.status);
                setProgress(response.data.progresses);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
        localStorage.removeItem("createInvoiceId");
    }

    function GetStatusHeight() {
        return window.innerHeight - 112;
    }

    function CopyToClipboard(text: string) {
        navigator.clipboard.writeText(text);
        enqueueSnackbar("The error message copied to clipboard", { variant: "success" });
    }

    return (
        <Stack direction={"row"}>
            <Stack
                direction={"column"}
                alignItems={"start"}>
                {/* Create invoice for all clients*/}
                <Stack
                    direction={"row"}
                    alignItems={"center"} >
                    <p style={{ width: '200px' }}>Create for specific client: </p>
                    <Switch
                        checked={isForSpecificClient}
                        onChange={SelectForSpecificClient}
                        size="small" />
                </Stack>

                {/* Billing per */}
                {isForSpecificClient ?
                    <>
                        <span style={{ width: '200px' }}>Select clients: </span>

                        <FormControl sx={{ minWidth: '246px', width: '246px', marginBottom: "10px" }}>
                            <Select
                                multiple
                                value={specificClients}
                                onChange={(event: any) => setSpecificClients(event.target.value)}
                                size="small"
                                sx={{ height: "40px" }}
                                input={<OutlinedInput label="Tag" />}
                                renderValue={(selected) => selected.join(', ')}>
                                {allClients.map((item: string, index: number) => (
                                    <MenuItem
                                        key={index}
                                        value={item}>
                                        <Checkbox sx={{ padding: "0px" }} checked={specificClients.indexOf(item) > -1} />
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                    : null}

                <span>Select month</span>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        format="MM/YYYY"
                        views={['year', 'month']}
                        openTo={'month'}
                        value={month}
                        onChange={(param: any) => {
                            setMonth(param);
                            monthCopy.current = dayjs(param);
                        }}
                    />
                </LocalizationProvider>

                <Button
                    variant="outlined"
                    onClick={CreateBillingInvoices}
                    sx={{ marginTop: "15px" }}>
                    Create invoices
                </Button>
            </Stack>
            <Stack spacing={0}
                sx={{ height: GetStatusHeight(), width: "100%", marginLeft: "15px", overflowX: 'auto', overflowY: 'auto', }}>
                <h3>{status}</h3>

                {progress.map((item, index) => (
                    <Stack
                        key={index}
                        direction={"row"}
                        spacing={2}>
                        <span>{item.username}</span>
                        {item.status === "Waiting" ? <HourglassBottomTwoToneIcon htmlColor="yellow" /> : null}
                        {item.status === "InProgress" ? <AutorenewIcon htmlColor="orange" /> : null}
                        {item.status === "Completed" ? <CheckCircleIcon htmlColor="green" /> : null}
                        {
                            item.status === "Error" ?
                                <>
                                    <ErrorIcon htmlColor="red" />
                                    <IconButton size="small" onClick={() => CopyToClipboard(item.errorDescription)}>
                                        <ContentCopyIcon />
                                    </IconButton>
                                </>
                                : null
                        }
                    </Stack>
                ))}
            </Stack>
        </Stack>
    )
}

export default CreateXeroInvoices;